import React from "react";
import { Chat } from "../../types/conversations";
import { ChatSearch } from "./ChatSearch";
import { ChatListItem } from "./ChatListItem";
import { SupportChatListItem } from "./SupportChatListItem";
import Skeleton from "../ui/Skeleton";
import { FiMessageSquare, FiSearch } from "react-icons/fi";

interface ChatListProps {
  chats: Chat[];
  selectedChatId: number | null;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  onChatSelect: (chatId: number | null) => void;
  loading?: boolean;
  onRefresh?: () => void;
  onScroll?: () => void;
  chatsContainerRef?: React.RefObject<HTMLDivElement>;
  isLoadingMore?: boolean;
  /** Se true, o usuário logado é profissional (badge "Aluno"); se false, é aluno (badge "Meu professor") */
  viewerIsProfessional?: boolean;
  /** Item fixo exibido no topo da lista (ex: Suporte Easyfit) */
  pinnedChat?: Chat;
}

const ChatListSkeleton: React.FC = () => {
  return (
    <div className="flex items-center gap-3 px-3 py-3 border-b border-gray-100 dark:border-gray-700 md:px-4">
      <Skeleton className="h-11 w-11 rounded-full md:h-12 md:w-12" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between mb-1">
          <Skeleton className="h-4 w-24 md:w-32" />
          <Skeleton className="h-3 w-10 md:w-12" />
        </div>
        <div className="flex items-center justify-between">
          <Skeleton className="h-3 w-32 md:w-48" />
          <Skeleton className="h-5 w-5 rounded-full" />
        </div>
      </div>
    </div>
  );
};

const EmptyChatsListState: React.FC<{ isSearch: boolean }> = ({ isSearch }) => (
  <div className="flex flex-col items-center justify-center flex-1 px-6 py-12 text-center select-none">
    <div className="mb-4 flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 dark:bg-gray-700/60 text-gray-400 dark:text-gray-500">
      {isSearch ? (
        <FiSearch size={28} strokeWidth={1.5} />
      ) : (
        <FiMessageSquare size={28} strokeWidth={1.5} />
      )}
    </div>
    <p className="text-sm font-semibold text-gray-600 dark:text-gray-300 mb-1">
      {isSearch ? "Nenhum resultado" : "Sem conversas ainda"}
    </p>
    <p className="text-xs text-gray-400 dark:text-gray-500 max-w-[180px] leading-relaxed">
      {isSearch
        ? "Tente outro nome ou palavra-chave."
        : "Quando você tiver conversas com alunos, elas aparecerão aqui."}
    </p>
  </div>
);

export const ChatList: React.FC<ChatListProps> = ({
  chats,
  selectedChatId,
  searchQuery,
  onSearchChange,
  onChatSelect,
  loading = false,
  onRefresh,
  onScroll,
  chatsContainerRef,
  isLoadingMore = false,
  viewerIsProfessional = false,
  pinnedChat,
}) => {
  return (
    <div className="w-full min-h-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 flex flex-col h-full md:w-96 md:border-b-0 md:border-r">
      <ChatSearch
        value={searchQuery}
        onChange={onSearchChange}
        onRefresh={onRefresh}
        onBack={() => onChatSelect(null)}
        hasSelectedChat={!!selectedChatId}
      />

      {/* Item fixo no topo (Suporte Easyfit) — sempre visível, fora do scroll */}
      {pinnedChat && !searchQuery.trim() && (
        <SupportChatListItem
          chat={pinnedChat}
          isSelected={selectedChatId === pinnedChat.id}
          onClick={() => onChatSelect(pinnedChat.id)}
        />
      )}

      <div
        ref={chatsContainerRef}
        className="conversations-scroll flex-1 min-h-0 overflow-y-auto"
        style={{
          WebkitOverflowScrolling: "touch",
          overscrollBehavior: "contain",
        }}
        onScroll={onScroll}
      >
        {loading && (
          <div>
            {[...Array(8)].map((_, i) => (
              <ChatListSkeleton key={i} />
            ))}
          </div>
        )}
        {!loading && chats.length === 0 && (
          <EmptyChatsListState isSearch={searchQuery.trim() !== ""} />
        )}
        {!loading && chats.length > 0 && (
          <>
            {chats.map((chat) => (
              <ChatListItem
                key={chat.id}
                chat={chat}
                isSelected={selectedChatId === chat.id}
                onClick={() => onChatSelect(chat.id)}
                viewerIsProfessional={viewerIsProfessional}
              />
            ))}
            {isLoadingMore && (
              <div>
                {[...Array(3)].map((_, i) => (
                  <ChatListSkeleton key={`loading-${i}`} />
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};
